import React, { useState, useEffect, useRef } from 'react';
import { X, BookOpen, GraduationCap, Hash, Calendar, Save } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { educationAPI } from '../api/axiosClient';
import { toast } from 'react-hot-toast';

interface ClassroomEditFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingClassroomId: string | null;
}

interface ClassroomFormData {
  name: string;
  grade: number;
  section: string;
  year: number;
}

const ClassroomEditForm: React.FC<ClassroomEditFormProps> = ({ isOpen, onClose, onSuccess, editingClassroomId }) => {
  const [classroomForm, setClassroomForm] = useState<ClassroomFormData>({
    name: '',
    grade: 1,
    section: '',
    year: new Date().getFullYear()
  });

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const hasLoadedData = useRef(false);

  useEffect(() => {
    if (isOpen && editingClassroomId && !hasLoadedData.current) {
      hasLoadedData.current = true;
      setIsLoading(true);

      const loadClassroomData = async () => {
        try {
          const response = await educationAPI.getClassroomDetails(editingClassroomId);
          
          if (response.success && response.data) {
            const classroomData = response.data;
            setClassroomForm({
              name: classroomData.name || '',
              grade: classroomData.grade || 1,
              section: classroomData.section || '',
              year: classroomData.year || new Date().getFullYear()
            });
          } else {
            console.error('Sınıf detayları yüklenemedi:', response.message);
            toast.error('Sınıf detayları yüklenirken bir hata oluştu: ' + response.message);
            onClose();
          }
        } catch (error) {
          console.error('Sınıf detayları yükleme hatası:', error);
          toast.error('Sınıf detayları yüklenirken bir hata oluştu');
          onClose();
        } finally {
          setIsLoading(false);
        }
      };

      loadClassroomData();
    } else if (!isOpen) {
      hasLoadedData.current = false;
    }
  }, [isOpen, editingClassroomId, onClose]);

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    // Sınıf Adı Validasyonu
    if (!classroomForm.name?.trim()) {
      newErrors.name = 'Sınıf adı zorunludur';
    } else if (classroomForm.name.trim().length < 2) {
      newErrors.name = 'Sınıf adı en az 2 karakter olmalıdır';
    } else if (classroomForm.name.trim().length > 50) {
      newErrors.name = 'Sınıf adı en fazla 50 karakter olabilir';
    } else if (!/^[a-zA-Z0-9\s\-.]+$/.test(classroomForm.name.trim())) {
      newErrors.name = 'Sınıf adı sadece harf, rakam, boşluk, tire ve nokta içerebilir';
    }

    // Sınıf Seviyesi Validasyonu
    if (!classroomForm.grade) {
      newErrors.grade = 'Sınıf seviyesi zorunludur';
    } else if (!Number.isInteger(classroomForm.grade)) {
      newErrors.grade = 'Sınıf seviyesi tam sayı olmalıdır';
    } else if (classroomForm.grade < 1 || classroomForm.grade > 12) {
      newErrors.grade = 'Sınıf seviyesi 1-12 arasında olmalıdır';
    }

    // Şube Validasyonu
    if (!classroomForm.section?.trim()) {
      newErrors.section = 'Şube zorunludur';
    } else if (classroomForm.section.trim().length > 10) {
      newErrors.section = 'Şube en fazla 10 karakter olabilir';
    } else if (!/^[a-zA-Z0-9]+$/.test(classroomForm.section.trim())) {
      newErrors.section = 'Şube sadece harf ve rakam içerebilir';
    }

    // Yıl Validasyonu
    if (!classroomForm.year) {
      newErrors.year = 'Yıl zorunludur';
    } else if (!Number.isInteger(classroomForm.year)) {
      newErrors.year = 'Yıl tam sayı olmalıdır';
    } else if (classroomForm.year < 2020 || classroomForm.year > 2030) {
      newErrors.year = 'Yıl 2020-2030 arasında olmalıdır';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: any) => {
    setClassroomForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingClassroomId) return;

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const requestData = {
        name: classroomForm.name,
        grade: classroomForm.grade,
        section: classroomForm.section,
        year: classroomForm.year
      };

      const response = await educationAPI.updateClassroom(editingClassroomId, requestData);

      if (response.success) {
        onSuccess();
        onClose();
      } else {
        toast.error(response.message || 'Sınıf güncellenirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Error updating classroom:', error);
      toast.error('Sınıf güncellenirken bir hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              Sınıf Düzenle
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Form */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B51E0]"></div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Sınıf Adı */}
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                    <BookOpen className="w-4 h-4 text-gray-500" />
                    <span>Sınıf Adı <span className="text-red-500">*</span></span>
                  </label>
                  <Input
                    type="text"
                    value={classroomForm.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Sınıf adını girin (örn: 1-A)"
                    className={`mt-1 ${errors.name ? 'border-red-500' : ''}`}
                  />
                  {errors.name && (
                    <p className="text-sm text-red-600 mt-1">{errors.name}</p>
                  )}
                </div>

                {/* Sınıf Seviyesi */}
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                    <GraduationCap className="w-4 h-4 text-gray-500" />
                    <span>Sınıf Seviyesi <span className="text-red-500">*</span></span>
                  </label>
                  <Input
                    type="number"
                    value={classroomForm.grade}
                    onChange={(e) => handleInputChange('grade', parseInt(e.target.value) || 1)}
                    placeholder="Sınıf seviyesi (1-12)"
                    className={`mt-1 ${errors.grade ? 'border-red-500' : ''}`}
                  />
                  {errors.grade && (
                    <p className="text-sm text-red-600 mt-1">{errors.grade}</p>
                  )}
                </div>

                {/* Şube */}
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                    <Hash className="w-4 h-4 text-gray-500" />
                    <span>Şube <span className="text-red-500">*</span></span>
                  </label>
                  <Input
                    type="text"
                    value={classroomForm.section}
                    onChange={(e) => handleInputChange('section', e.target.value)}
                    placeholder="Şube (örn: A, B, C)"
                    className={`mt-1 ${errors.section ? 'border-red-500' : ''}`}
                  />
                  {errors.section && (
                    <p className="text-sm text-red-600 mt-1">{errors.section}</p>
                  )}
                </div>

                {/* Yıl */}
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span>Yıl <span className="text-red-500">*</span></span>
                  </label>
                  <Input
                    type="number"
                    value={classroomForm.year}
                    onChange={(e) => handleInputChange('year', parseInt(e.target.value) || new Date().getFullYear())}
                    placeholder="Eğitim yılı"
                    className={`mt-1 ${errors.year ? 'border-red-500' : ''}`}
                  />
                  {errors.year && (
                    <p className="text-sm text-red-600 mt-1">{errors.year}</p>
                  )}
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  İptal
                </Button>
                <Button
                  type="submit"
                  className="bg-[#9B51E0] hover:bg-[#8B41D0]"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Güncelleniyor...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Kaydet
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClassroomEditForm;
